import { format } from 'date-fns';
import { ru as locale } from 'date-fns/locale/ru';
import type { ProductType } from '@periodica/products';
import type { AnalyticsEvent } from './types';

declare global {
  interface Window {
    ekEvents: any;
  }
}

export type EnkodProductRawData = {
  type: ProductType;
  name?: string;
  count?: number;
  productId?: string;
};

export type EnkodOrderRawData = {
  user_name: string;
  orderId: string;
  sum: number;
  items: EnkodProductRawData[];
};

type EnkodProviderUser = {
  id?: number | string;
  email?: string;
  phone?: string;
};

const allEventsParams = () => ({
  source: 'web',
  // формат, в котором энкод узнает дату как дату
  date: format(new Date(), 'y-MM-dd HH:mm', { locale }),
});

const logDevEvent = ({
  eventName,
  eventData,
  err,
}: {
  eventName: string;
  eventData: any;
  err: any;
}) => {
  if (process.env.NODE_ENV === 'development') {
    if (eventName !== '') {
      console.log(`Enkod event fired: '${eventName}'`);
      Object.keys(eventData ?? {}).length && console.log('data:', { ...eventData });
      console.log('\n');
    }
  } else {
    console.warn('Something went wrong with enkod ekEvents: ', err);
  }
};

export class EnkodProvider {
  private user: EnkodProviderUser | null = null;

  async setUser(user: { id?: number | string; email?: string; phone?: string } | null) {
    if (user) {
      this.user = { ...user };
      const eventData = {
        fields: {
          ...(this.user.email && { email: this.user.email }),
          ...(this.user.phone && { phone: this.user.phone }),
        },
        ...(this.user.id && { extraFields: { user_id: this.user.id } }),
      };
      try {
        const result = window.ekEvents.push('subscribe', { ...eventData });
        return result;
      } catch (err) {
        logDevEvent({ eventName: 'subscribe', eventData, err });
        if (process.env.NODE_ENV === 'development') return { status: 200 };
      }
    } else {
      this.user = null;
    }
  }

  private logEvent(event: AnalyticsEvent) {
    if (process.env.ENKOD_ENABLED) {
      const eventData = {
        ...(this.user?.email && { email: this.user?.email }),
        ...(this.user?.phone && { phone: this.user?.phone }),
        params: {
          ...event.data,
          ...(this.user?.id && { user_id: this.user?.id }),
        },
      };
      try {
        // ekEvents.push('event', {email:'my@email.com', phone:'79000000000', params: {param1: 'value1'}});
        window.ekEvents.push(event.name, { ...eventData });
      } catch (err) {
        logDevEvent({ eventName: event.name, eventData, err });
      }
    }
  }

  logCreateProject(data: EnkodProductRawData) {
    const eventData = {
      ...data,
      ...allEventsParams(),
    };
    this.logEvent({ name: 'create_project', data: eventData });
  }

  logPageOpen() {
    try {
      window.ekEvents.push('pageOpen');
    } catch (err) {
      logDevEvent({ eventName: 'pageOpen', eventData: {}, err });
    }
  }

  // тут передаем и пользовательское и стандартное событие, они нужны из-за разных параметров
  logAddToCart(data: EnkodProductRawData) {
    const eventOldData = {
      ...data,
      ...allEventsParams(),
    };
    this.logEvent({ name: 'product_add', data: eventOldData });

    const eventData = {
      productId: data.name,
      type: data.type,
      ...(this.user?.id && { user_id: this.user?.id }),
      ...allEventsParams(),
    };
    try {
      window.ekEvents.push('productAdd', { ...eventData });
    } catch (err) {
      logDevEvent({ eventName: 'productAdd', eventData, err });
    }
  }

  // тут передаем и пользовательское и стандартное событие, они нужны из-за разных параметров
  logRemoveFromCart(data: EnkodProductRawData) {
    const eventOldData = {
      ...data,
      ...allEventsParams(),
    };
    this.logEvent({ name: 'product_remove', data: eventOldData });
    const eventData = {
      productId: data.name,
      type: data.type,
      ...(this.user?.id && { user_id: this.user?.id }),
      ...allEventsParams(),
    };
    try {
      window.ekEvents.push('productRemove', { ...eventData });
    } catch (err) {
      logDevEvent({ eventName: 'productRemove', eventData, err });
    }
  }

  async logPurchase(
    data: EnkodOrderRawData,
    subscribtionData: { needSubscribe: boolean; phone: string; email: string }
  ) {
    const { needSubscribe, phone, email } = subscribtionData;

    const sendPurchase = (data: EnkodOrderRawData) => {
      const eventData = {
        ...(this.user?.id && { user_id: this.user?.id }),
        ...data,
        ...allEventsParams(),
      };
      try {
        window.ekEvents.push('productBuy', { ...eventData });
      } catch (err) {
        logDevEvent({ eventName: 'productBuy', eventData, err });
      }
    };

    if (needSubscribe) {
      try {
        const setUserResult = await this.setUser({
          phone,
          email,
        });
        if (setUserResult.status === 200) {
          sendPurchase(data);
        }
      } catch (err) {
        console.warn('Something went wrong with enkod subscribe: ', err);
      }
    } else {
      sendPurchase(data);
    }
  }
}
